import {
  protectedAxios,
  protectedContentAxios,
  publicAxios,
  publicContentAxios,
} from '@/lib/axiosInstance';
import {
  CreateQuestionRequest,
  CreateQuestionResponse,
  DeleteQuestionRequest,
  DeleteQuestionResponse,
  QuestionDetailResponse,
  QuestionsListingRequest,
  QuestionsListingResponse,
  ReportReasonResponse,
  ReportRequest,
  ReportResponse,
  SearchQuestionsRequest,
  SearchQuestionsResponse,
  UpdateQuestionRequest,
  UpdateQuestionResponse,
} from '@/types/api/question';

export const createQuestion = async (
  payload: CreateQuestionRequest
): Promise<CreateQuestionResponse> => {
  const response = await protectedContentAxios.post<CreateQuestionResponse>(
    '/questions',
    payload
  );

  return response.data;
};

export const updateQuestion = async (
  payload: UpdateQuestionRequest
): Promise<UpdateQuestionResponse> => {
  const response = await protectedContentAxios.put<UpdateQuestionResponse>(
    `/questions/${payload.id}`,
    payload
  );

  return response.data;
};

export const deleteQuestion = async (
  payload: DeleteQuestionRequest
): Promise<DeleteQuestionResponse> => {
  const response = await protectedContentAxios.delete<DeleteQuestionResponse>(
    `/questions/${payload.id}`
  );

  return response.data;
};

export const getQuestionFromSlug = async (
  slug: string
): Promise<QuestionDetailResponse> => {
  const response = await publicContentAxios.get<QuestionDetailResponse>(
    `/questions/slug/${slug}`
  );
  return response.data;
};

export const getQuestions = async (
  payload: QuestionsListingRequest,
  isProtected: boolean = false,
  source?: 'add-story' | 'activity'
): Promise<QuestionsListingResponse> => {
  const queryParams = new URLSearchParams({
    page: String(payload.page),
    limit: String(payload.limit),
    categoryId: payload.categoryId?.join(',') || '',
    status: payload.status || '',
    type: payload.type || '',
    userId: payload.userId || '',
    discussionCategoryId: Array.isArray(payload.discussionCategoryId)
      ? payload.discussionCategoryId.join(',')
      : payload.discussionCategoryId || '',
    industryIds: payload.industryIds?.join(',') || '',
    occupationIds: payload.occupationIds?.join(',') || '',
    search: payload.search || '',
    trending: payload.trending || '',
    recentActivity: payload.recentActivity || '',
  });
  const axiosInstance = isProtected
    ? protectedContentAxios
    : publicContentAxios;

  // Use personalized route if:
  // 1. User is authenticated (isProtected is true)
  // 2. Request is specifically from add-story page
  const shouldUsePersonalizedRoute = isProtected && source === 'add-story';

  const url = shouldUsePersonalizedRoute
    ? `/questions/personalized?${queryParams}`
    : `/questions?${queryParams}`;

  const response = await axiosInstance.get(url);

  return response.data;
};

// Review later. Need to confirm about content based api

export const getRelatedQuestions = async (
  categoryId: string,
  limit: number = 5,
  theme?: string
): Promise<QuestionsListingResponse> => {
  console.log(
    `[getRelatedQuestions] Fetching related questions for category: ${categoryId}, theme: ${theme}, limit: ${limit}`
  );

  // If theme is provided, use Elasticsearch for better theme matching
  if (theme && process.env.NEXT_PUBLIC_ENHANCED_SEARCH_ENABLED === 'true') {
    try {
      console.log(
        `[getRelatedQuestions] Using Elasticsearch for theme-based matching`
      );

      // Import the search service axios
      const { searchServiceAxios } = await import('@/lib/searchServiceAxios');
      const { getCurrentTenantId } = await import('@/lib/tenant');

      const tenantId = getCurrentTenantId();

      // Use the new related content endpoint
      const searchParams = {
        theme: theme,
        type: 'question',
        categoryId: categoryId,
        tenantId: tenantId,
        limit: limit,
      };

      const response = await searchServiceAxios.get('/search/related', {
        params: searchParams,
        timeout: 5000,
      });

      if (response.data.success && response.data.data.hits) {
        const questions = response.data.data.hits.map((hit: any) => ({
          id: hit._source.id,
          questionTitle: hit._source.title,
          questionDescription: hit._source.description,
          createdDate: hit._source.createdDate,
          questionType: hit._source.questionType || 'NORMAL',
          modifiedDate: hit._source.modifiedDate,
          slug: hit._source.slug,
          isAnonymous: hit._source.isAnonymous || false,
          isReported: hit._source.isReported || false,
          storyCount: hit._source.storyCount || 0,
          author: hit._source.author,
          category: hit._source.category,
        }));

        console.log(
          `[getRelatedQuestions] Elasticsearch returned ${questions.length} questions`
        );
        return {
          statusCode: 200,
          message: 'Success',
          data: questions.slice(0, limit),
          totalCount: questions.length,
          page: 1, // or the actual page number if you support pagination
          limit: limit, // reuse the input limit
          success: true, // assuming the request succeeded
        };
      }
    } catch (error) {
      console.warn(
        `[getRelatedQuestions] Elasticsearch failed, falling back to traditional API:`,
        error
      );
    }
  }

  // Fallback to traditional API
  const queryParams = new URLSearchParams({
    page: '1',
    limit: String(limit),
    categoryId: categoryId,
    status: 'PUBLISHED',
  });

  console.log(
    `[getRelatedQuestions] Using traditional API with params:`,
    Object.fromEntries(queryParams.entries())
  );

  const response = await publicContentAxios.get<QuestionsListingResponse>(
    `/questions?${queryParams}`
  );

  console.log(
    `[getRelatedQuestions] Traditional API returned ${response.data.data?.length || 0} questions`
  );
  return response.data;
};
export const report = async (
  payload: ReportRequest
): Promise<ReportResponse> => {
  const response = await protectedAxios.post<ReportResponse>(
    '/user/report',
    payload
  );

  return response.data;
};

export const getReportReasons = async (): Promise<ReportReasonResponse> => {
  const response = await protectedAxios.get<ReportReasonResponse>(
    '/user/report-reason'
  );

  return response.data;
};

// search questions
export const searchQuestions = async (
  payload: SearchQuestionsRequest
): Promise<SearchQuestionsResponse> => {
  const response = await publicContentAxios.get<SearchQuestionsResponse>(
    '/questions',
    { params: payload }
  );

  return response.data;
};
